import { useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { MdOutlineImage } from "react-icons/md";
import { ensureFullscreen } from "../../utils/fullScreenHelper";

// Module-level guard: Electron's ipcRenderer.on stacks listeners on each call.
// Register once for the page lifetime regardless of how many instances are mounted.
let electronListenerRegistered = false;
// Set at click time to the specific instance that opened the browser.
// Cleared after the result arrives so stale callbacks are never called.
let electronPendingCallback = null;

// Accepted MIME types
const ACCEPTED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const validateFile = (file) => {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return "Unsupported file type. Allowed: PDF, PNG, JPG, JPEG, DOCX, XLSX.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File exceeds the 5 MB size limit.";
  }
  return null;
};

export default function FileUploadZone({ onFileSelect, disabled }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileSelectedRef = useRef(false);
  const isElectron = window.hubbleAPI?.isKiosk?.() ?? false;

  // Register Electron upload listener once for the page lifetime.
  useEffect(() => {
    if (!isElectron) return;
    if (electronListenerRegistered) return;
    electronListenerRegistered = true;

    window.hubbleAPI.onUploadFileSelected((result) => {
      const cb = electronPendingCallback;
      electronPendingCallback = null; // consume — prevent re-delivery on future events
      if (!cb) return;
      if (!result.success) {
        let msg;
        if (result.reason === "not_whitelisted") {
          msg = "File must be from an allowed folder";
        } else if (result.reason === "too_large") {
          msg = `File too large. Max: ${((result.maxBytes ?? 0) / 1024 / 1024).toFixed(0)} MB`;
        } else {
          msg = "Could not select file. Please try again.";
        }
        cb(null, msg);
        return;
      }
      const blob = new Blob([result.data], { type: result.type });
      const file = new File([blob], result.name, { type: result.type });
      const error = validateFile(file);
      cb(error ? null : file, error);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore fullscreen when file picker is dismissed without selecting (cancel)
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    // Modern browsers (Chrome 113+, Firefox 91+): cancel event fires on dismiss
    const onCancel = () => ensureFullscreen();
    input.addEventListener("cancel", onCancel);
    return () => input.removeEventListener("cancel", onCancel);
  }, []);

  const handleFile = (file) => {
    const error = validateFile(file);
    onFileSelect(error ? null : file, error);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      fileSelectedRef.current = true;
      handleFile(file);
    }
    // Reset input so the same file can be re-selected after deletion
    e.target.value = "";
  };

  const handleClick = () => {
    if (disabled) return;
    if (isElectron) {
      electronPendingCallback = onFileSelect; // capture which question triggered this
      window.hubbleAPI.openUploadBrowser();
      return;
    }
    fileSelectedRef.current = false;
    inputRef.current?.click();
    // Fallback for browsers that don't support the cancel event:
    // window regains focus when the picker closes (select or cancel)
    const onFocus = () => {
      if (!fileSelectedRef.current) ensureFullscreen();
    };
    window.addEventListener("focus", onFocus, { once: true });
  };

  return (
    <Box
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      aria-label="Upload file"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        border: `2px dashed ${isDragging ? "#2d2fb5" : "#3F41D1"}`,
        borderRadius: "8px",
        p: "14px 20px",
        cursor: disabled ? "not-allowed" : "pointer",
        backgroundColor: isDragging
          ? "rgba(63,65,209,0.06)"
          : "rgba(63,65,209,0.02)",
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 0.2s, border-color 0.2s",
        userSelect: "none",
        width: { xs: "100%", sm: "fit-content" },
        minWidth: { sm: "340px" },
        "&:focus-visible": {
          outline: "2px solid #3F41D1",
          outlineOffset: "2px",
        },
      }}
    >
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
        style={{ display: "none" }}
        onChange={handleInputChange}
        disabled={disabled}
        aria-hidden="true"
      />

      {/* Icon */}
      <MdOutlineImage size={38} color="#3F41D1" style={{ flexShrink: 0 }} />

      {/* Text */}
      <Box>
        <Typography sx={{ fontSize: "14px", color: "#1e1e1e" }}>
          Drag and drop a file or&nbsp;
          <Box
            component="span"
            sx={{
              color: "#3F41D1",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            browse file
          </Box>
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "gray", mt: 0.3 }}>
          (PDF, PNG, JPG, JPEG, DOCX – up to 5 MB)
        </Typography>
      </Box>
    </Box>
  );
}
